import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  UploadTaskSnapshot,
} from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/solid'; // Example icons

interface ImageUploadInputProps {
  onUploadComplete: (urls: string[]) => void;
  maxFiles?: number;
  label?: string;
  storagePath?: string; // Optional path prefix in Firebase Storage
  autoUpload?: boolean;
  compactLayout?: boolean;
  className?: string;
}

interface FileUploadStatus {
  file: File;
  preview: string;
  progress: number;
  error: string | null;
  url: string | null;
  status: 'pending' | 'compressing' | 'uploading' | 'completed' | 'error';
}

const ImageUploadInput: React.FC<ImageUploadInputProps> = ({
  onUploadComplete,
  maxFiles = 5,
  label = 'Dodaj zdjęcia',
  storagePath = 'uploads', // Default storage path
  autoUpload = false,
  compactLayout = false,
  className = '',
}) => {
  const [uploadStatuses, setUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFriendlyUploadError = (error: any): string => {
    const code = typeof error?.code === 'string' ? error.code : '';
    switch (code) {
      case 'storage/unauthorized':
        return 'Brak uprawnień do przesyłania pliku w tym czacie.';
      case 'storage/canceled':
        return 'Przesyłanie zostało anulowane.';
      case 'storage/retry-limit-exceeded':
      case 'storage/unknown':
        return 'Nie udało się przesłać pliku. Spróbuj ponownie za chwilę.';
      default:
        return 'Nie udało się przesłać pliku.';
    }
  };

  // Define compression options in the component scope
  const compressionOptions = useMemo(
    () => ({
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.7,
    }),
    []
  );

  // Cleanup object URLs on unmount or when statuses change
  useEffect(() => {
    const previews = uploadStatuses.map((s) => s.preview);
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, [uploadStatuses]);

  const updateFileStatus = useCallback(
    (fileName: string, updates: Partial<Omit<FileUploadStatus, 'file' | 'preview'>>) => {
      setUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file.name === fileName ? { ...status, ...updates } : status
        )
      );
    },
    []
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const newFiles = Array.from(files);
      const totalFiles = uploadStatuses.length + newFiles.length;

      if (totalFiles > maxFiles) {
        alert(`Możesz wybrać maksymalnie ${maxFiles} plików.`);
        // Optionally trim the selection
        // newFiles = newFiles.slice(0, maxFiles - uploadStatuses.length);
        return; // Or handle differently
      }

      const newStatuses: FileUploadStatus[] = newFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        progress: 0,
        error: null,
        url: null,
        status: 'pending',
      }));

      setUploadStatuses((prevStatuses) => [...prevStatuses, ...newStatuses]);

      // Clear the input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [maxFiles, uploadStatuses.length]
  );

  const removeFile = useCallback((fileName: string) => {
    setUploadStatuses((prevStatuses) => {
      const statusToRemove = prevStatuses.find((s) => s.file.name === fileName);
      if (statusToRemove) {
        URL.revokeObjectURL(statusToRemove.preview);
      }
      return prevStatuses.filter((s) => s.file.name !== fileName);
    });
  }, []);

  const uploadSingleFile = useCallback(
    async (status: FileUploadStatus): Promise<{ url?: string; success: boolean }> => {
      try {
        updateFileStatus(status.file.name, { status: 'compressing', error: null });
        const compressedFile = await imageCompression(status.file, compressionOptions);
        const fileName = `${Date.now()}-${status.file.name}`;
        const filePath = `${storagePath}/${fileName}`;
        const storageRef = ref(getStorage(), filePath);

        updateFileStatus(status.file.name, { status: 'uploading', progress: 0 });

        const downloadURL = await new Promise<string>((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, compressedFile);

          uploadTask.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              updateFileStatus(status.file.name, { progress: Math.round(progress) });
            },
            (error) => {
              console.error('Upload Error:', error);
              updateFileStatus(status.file.name, {
                status: 'error',
                error: getFriendlyUploadError(error),
                progress: 0,
              });
              reject(error);
            },
            async () => {
              try {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                updateFileStatus(status.file.name, {
                  status: 'completed',
                  url,
                  progress: 100,
                });
                resolve(url);
              } catch (getUrlError) {
                console.error('Get Download URL Error:', getUrlError);
                updateFileStatus(status.file.name, {
                  status: 'error',
                  error: 'Błąd pobierania URL',
                  progress: 0,
                });
                reject(getUrlError);
              }
            }
          );
        });

        return { url: downloadURL, success: true };
      } catch (error: any) {
        console.error('Compression/Upload Error:', error);
        updateFileStatus(status.file.name, {
          status: 'error',
          error: getFriendlyUploadError(error),
          progress: 0,
        });
        return { success: false };
      }
    },
    [compressionOptions, storagePath, updateFileStatus]
  );

  const handleUpload = useCallback(async () => {
    const filesToUpload = uploadStatuses.filter((s) => s.status === 'pending');
    if (filesToUpload.length === 0) return;

    setIsProcessing(true);
    const uploadedUrls: string[] = [];
    let allUploadsSuccessful = true;

    for (const status of filesToUpload) {
      const result = await uploadSingleFile(status);
      if (result.url) uploadedUrls.push(result.url);
      if (!result.success) {
        allUploadsSuccessful = false;
      }
    }

    setIsProcessing(false);
    if (allUploadsSuccessful && uploadedUrls.length > 0) {
      onUploadComplete(uploadedUrls);
      // Optionally clear completed uploads from the list after successful callback
      // setUploadStatuses(prev => prev.filter(s => s.status !== 'completed'));
    } else if (uploadedUrls.length > 0) {
      // Handle partial success? Maybe call onUploadComplete only with successful URLs?
      console.warn('Niektóre pliki nie zostały przesłane pomyślnie.');
      // Decide if you want to call onUploadComplete even with partial success
      // onUploadComplete(uploadedUrls);
    }
  }, [uploadStatuses, uploadSingleFile, onUploadComplete]);

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const hasPendingFiles = uploadStatuses.some((s) => s.status === 'pending');

  useEffect(() => {
    if (!autoUpload || isProcessing || !hasPendingFiles) {
      return;
    }
    void handleUpload();
  }, [autoUpload, handleUpload, hasPendingFiles, isProcessing]);

  const rootContainerClass = compactLayout ? 'p-2.5 sm:p-3' : 'p-4';
  const dropZoneClass = compactLayout
    ? 'px-3.5 pt-3.5 pb-3.5 sm:px-4 sm:pt-4 sm:pb-4'
    : 'px-6 pt-5 pb-6';
  const iconClass = compactLayout
    ? 'mx-auto h-8 w-8 text-gray-400 sm:h-9 sm:w-9'
    : 'mx-auto h-12 w-12 text-gray-400';

  return (
    <div
      className={`${rootContainerClass} border border-gray-600 rounded-lg bg-gray-800 text-gray-300 ${className}`}
    >
      {label && <label className="block text-sm font-medium mb-2">{label}</label>}

      {/* File Input Trigger */}
      <div
        className={`flex items-center justify-center ${dropZoneClass} rounded-md border-2 border-gray-500 border-dashed cursor-pointer hover:border-green-500 ${uploadStatuses.length >= maxFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={uploadStatuses.length < maxFiles ? triggerFileInput : undefined}
        onKeyDown={(event) => {
          if (uploadStatuses.length >= maxFiles || isProcessing) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFileInput();
          }
        }}
        role="button"
        tabIndex={uploadStatuses.length >= maxFiles || isProcessing ? -1 : 0}
        aria-disabled={uploadStatuses.length >= maxFiles || isProcessing}
        aria-label={label || 'Wybierz zdjęcia'}
      >
        <div className="space-y-1 text-center">
          <ArrowUpTrayIcon className={iconClass} />
          <div className="flex text-sm leading-snug text-gray-400">
            <p className={compactLayout ? 'px-1 text-xs sm:text-sm' : 'pl-1'}>
              {compactLayout
                ? 'Kliknij, aby wybrać zdjęcia'
                : 'Przeciągnij i upuść pliki tutaj lub kliknij, aby wybrać'}
            </p>
          </div>
          <p className="text-xs leading-snug text-gray-500">
            PNG, JPG, GIF do {compressionOptions.maxSizeMB}MB po kompresji
          </p>
          <p className="text-xs text-gray-500">Pozostało: {maxFiles - uploadStatuses.length}</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileChange}
          disabled={uploadStatuses.length >= maxFiles || isProcessing}
        />
      </div>

      {/* File List & Previews */}
      {uploadStatuses.length > 0 && (
        <div className={compactLayout ? 'mt-3 space-y-2' : 'mt-4 space-y-2'}>
          <h4 className={compactLayout ? 'text-xs font-medium sm:text-sm' : 'text-sm font-medium'}>
            Wybrane pliki:
          </h4>
          <ul className={compactLayout ? 'space-y-2.5' : 'space-y-3'}>
            {uploadStatuses.map((status) => (
              <li
                key={status.file.name}
                className={`flex flex-wrap items-center justify-between rounded bg-gray-700 ${
                  compactLayout ? 'p-1.5 sm:p-2' : 'p-2'
                }`}
              >
                <div
                  className={
                    compactLayout
                      ? 'flex flex-grow items-center space-x-2.5'
                      : 'flex flex-grow items-center space-x-3'
                  }
                >
                  <img
                    src={status.preview}
                    alt={`Podgląd ${status.file.name}`}
                    className={
                      compactLayout
                        ? 'h-9 w-9 rounded border border-gray-600 object-cover'
                        : 'w-10 h-10 object-cover rounded border border-gray-600'
                    }
                  />
                  <div className="text-sm">
                    <p className="font-medium truncate max-w-xs">{status.file.name}</p>
                    <p className="text-xs text-gray-400">
                      {(status.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>

                <div
                  className={
                    compactLayout
                      ? 'ml-3 flex flex-shrink-0 items-center space-x-2'
                      : 'ml-4 flex flex-shrink-0 items-center space-x-2'
                  }
                >
                  {/* Status Indicator */}
                  <div
                    className={
                      compactLayout ? 'w-20 text-right text-[11px]' : 'w-24 text-xs text-right'
                    }
                  >
                    {status.status === 'pending' && (
                      <span className="text-yellow-400 flex items-center justify-end">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Oczekuje
                      </span>
                    )}
                    {status.status === 'compressing' && (
                      <span className="text-blue-400">Kompresja...</span>
                    )}
                    {status.status === 'uploading' && (
                      <div className="w-full bg-gray-600 rounded-full h-1.5">
                        <div
                          className="bg-green-500 h-1.5 rounded-full"
                          style={{ width: `${status.progress}%` }}
                        ></div>
                      </div>
                    )}
                    {status.status === 'completed' && (
                      <span className="text-green-500 flex items-center justify-end">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Gotowe
                      </span>
                    )}
                    {status.status === 'error' && (
                      <span
                        className="text-red-500 flex items-center justify-end"
                        title={status.error || ''}
                      >
                        <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                        Błąd
                      </span>
                    )}
                  </div>

                  {/* Remove Button */}
                  {(status.status === 'pending' || status.status === 'error') && !isProcessing && (
                    <button
                      type="button"
                      onClick={() => removeFile(status.file.name)}
                      className="p-1 text-gray-400 hover:text-red-500 focus:outline-none"
                      aria-label="Usuń plik"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {/* Display error message for this specific file, spanning full width below */}
                {status.error && status.status === 'error' && (
                  <p className="mt-1 w-full pl-12 text-left text-xs text-red-500 sm:pl-14">
                    {status.error}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Upload Button */}
      {hasPendingFiles && !autoUpload && (
        <div className={compactLayout ? 'mt-3 text-right' : 'mt-4 text-right'}>
          <button
            type="button"
            onClick={handleUpload}
            disabled={isProcessing || !hasPendingFiles}
            className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isProcessing
              ? 'Przetwarzanie...'
              : `Prześlij ${uploadStatuses.filter((s) => s.status === 'pending').length} plików`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUploadInput;
